<template>
    <v-card
        id="scroller"
        class="elevation-0 mb-4"
        width="89vw"
        :max-width="$vuetify.breakpoint.mdAndUp ? '450px' : '100%'"
    >
        <template v-if="!showCarousel && !showConfirmation">
            <v-card-text>
                <h2 class="text-left mb-4">
                    Select your preferred bank
                </h2>

                <v-alert
                    color="primary"
                    class="body-2 mb-0 px-6"
                    tile
                    text
                    dense
                >
                    Clicking a bank's logo will redirect you to your bank's login portal page, where you make payment for your transfer to the provided {{ paymentServiceName }} email.
                </v-alert>

                <v-autocomplete
                    v-model="selectedBank"
                    class="mt-4"
                    prepend-inner-icon="mdi-magnify"
                    label="Enter bank name"
                    item-text="name"
                    clearable
                    no-data-text="Bank not found? Pay directly from your Zelle app."
                    return-object
                    :items="filteredPaymentTypes"
                >
                    <template v-slot:item="{ item: bank }">
                        <v-list-item-avatar
                            color="white"
                            :rounded="false"
                        >
                            <v-img
                                class="ma-4"
                                max-width="150"
                                height="55"
                                :src="$_getPaymentLogoPath(bank.code)"
                                contain
                                tile
                            />
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title
                                v-html="bank.name"
                            />
                        </v-list-item-content>
                    </template>
                    <template #no-data>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title class="text-wrap">
                                    Bank not found? Pay directly from your Zelle app.
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-autocomplete>
            </v-card-text>
            <!-- <div class="alert"> -->
            <v-alert
                type="warning"
                class="body-2 mb-0 px-6 mx-4"
                text
                dense
            >
                You can ONLY make payment with a bank account registered/authenticated on Kaoshi.
                Any payment made by an unregistered bank account would not be acknowledged.
            </v-alert>
            <!-- </div> -->
            <v-card-actions class="px-4 mt-4">
                <v-btn
                    class="text-capitalize px-6 rounded-lg cancel"
                    text
                    color="red darken-2"
                    large
                    @click="$emit('close')"
                >
                    Cancel
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="selectedBank"
                    class="text-capitalize px-10 rounded-lg"
                    color="primary"
                    dark
                    depressed
                    large
                    @click.native="proceed"
                >
                    Proceed
                </v-btn>
            </v-card-actions>
        </template>

        <third-party-offer-guide
            v-else-if="showConfirmation"
            :order-type="acceptedOrder.type"
            :matcher-currency="acceptedOrder.matcher_currency"
            :company_logo="deliveryMethodLogo"
            @proceed="handleBankSelected"
        />

        <transfer-guide
            v-else
            :order="acceptedOrder"
            :posters-beneficiary="postersBeneficiaryInfo"
            :showP2PSteps="true"
            :total-slides="7"
            @proceed="showConfirmation = true"
        />
    </v-card>
</template>

<script>
    import ThirdPartyOfferGuide from './3po-guide.vue';
    import TransferGuide from './transfer-guide.vue';

    const currency = require('../../../modules/currency');

    export default {
        components: {
            TransferGuide,
            ThirdPartyOfferGuide,
        },
        data,
        props: {
            acceptedOrder: Object,
            postersBeneficiaryInfo: Object,
        },
        computed: getComputed(),
        methods: getMethods(),
        mounted,
    };

    function data() {
        return {
            loading: false,
            payment_type: '',
            payment_type_name: '',
            recipient: {},
            banks: [],
            pageTitle: 'Select your bank',
            search: '',
            selectedBank: null,
            showCarousel: false,
            showConfirmation: false,
        };
    }
    function mounted() {
        const vm = this;
        setupUiElements();

        function setupUiElements() {
            vm.$store.commit('pageHeaderClass', 'request');
            vm.$store.commit('pageHeaderTitle', vm.pageTitle);
            vm.$store.commit('mobileMenuIsVisible', false);
            vm.$store.commit('closeButtonIsVisible', true);
            vm.$store.commit('hideLogoPart', false);
            vm.positionBottomButton(13);
            vm.$store.commit('addLogoPartClass', 'add-order-payment-recipient');
            vm.recipient = {};
        }

        function addDummyDataToPaymentTypes() {
            this.acceptedOrder.matcher_currency
        }
    }

    function getMethods() {
        return {
            handleBankSelected,
            proceed() {
                if (this.$store.state.hideTransferInstructions) {
                    return this.handleBankSelected();
                }

                this.showConfirmation = false;
                this.showCarousel = true;
            },
        };

        function handleBankSelected() {
            const url = this.selectedBank.website;
            this.popupCenter(url);
            this.$emit('bankSelected', url);

            // We need to give the window time to open before we can leave this page
            setTimeout(() => {
                this.$router.push('/transfers');
            }, 1000);
        }
    }

    function getComputed() {
        return {
            matcherCurrency,
            matcherCurrencyPaymentTypes,
            filteredPaymentTypes,
            show: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                },
            },
            deliveryMethodLogo,
            paymentServiceName() {
                if (this.acceptedOrder.type === 'p2p'
                    && this.acceptedOrder.matcher_currency === 'CAD') {
                    return 'Interac';
                }
                return 'Zelle';
            },
        };

        function deliveryMethodLogo() {
            const { method } = this.postersBeneficiaryInfo;
            const imageName = `${method.toLowerCase()}_payment`;
            return this.$_getPaymentLogoPath(imageName);
        }

        function matcherCurrency() {
            return currency.findCurrency(
                this.acceptedOrder.matcher_currency,
            );
        }

        function matcherCurrencyPaymentTypes() {
            return this.matcherCurrency.payment_types;
        }

        function filteredPaymentTypes() {
            const vm = this;
            return this.matcherCurrencyPaymentTypes
                .filter(function(bank) {
                    return bank.name.toLowerCase()
                        .indexOf(vm.search.toLowerCase())>=0;});
        }
    }
</script>
<style>
    .alert {
        position: absolute;
        bottom: 0;
        left: 0;
    }

</style>

<style scoped>
    .cancel {
        background-color: #FFEDD3;
    }

    .v-autocomplete >>> .v-input__control {
        height: 45px;
    }
</style>
